"use client";

import type { ReactNode } from "react";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";
import type { MangalDosha } from "./mangalDoshaResult";

function TriggeringPlanetField({ planet, house }: { planet: string; house: number }) {
  const id = planet.toLowerCase();
  const handlers = useExplainableHandlers("mangal_dosha_result");
  const { data, isLoading, error, refetch } = useExplainer("planet", id, "medium", {
    enabled: false,
  });
  return (
    <p className="text-[14px]" style={{ color: "var(--ish-dark)" }}>
      <Explainable
        type="planet"
        id={id}
        intensity="medium"
        triggerLabel={planet.charAt(0).toUpperCase() + planet.slice(1)}
        data={data}
        loading={isLoading}
        error={error ?? null}
        onRequestData={() => void refetch()}
        {...handlers}
      />{" "}
      {/* inert-by-design: house number gives spatial context for the planet's placement; planet above is Explainable */}
      in House {house}
    </p>
  );
}

export default function MangalDoshaDetails({ dosha }: { dosha: MangalDosha }) {
  return (
    <div className="space-y-4">
      {dosha.triggering_planet && (
        <MangalDetailSection title="Mars Placement">
          <TriggeringPlanetField
            planet={dosha.triggering_planet}
            house={dosha.triggering_house ?? 0}
          />
        </MangalDetailSection>
      )}
      {dosha.description && (
        <MangalDetailSection title="Description">
          <p className="text-[14px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
            {dosha.description}
          </p>
        </MangalDetailSection>
      )}
      <MangalDetailSection title="Cancellation Check">
        <p className="text-[14px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
          {cancellationText(dosha)}
        </p>
      </MangalDetailSection>
      <MangalRemedies dosha={dosha} />
    </div>
  );
}

function MangalDetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <span
        className="block text-[12px] font-semibold uppercase tracking-wider mb-1"
        style={{ color: "var(--ish-text-faint)" }}
      >
        {title}
      </span>
      {children}
    </div>
  );
}

function cancellationText(dosha: MangalDosha) {
  if (dosha.cancellation_applied) {
    return (
      dosha.cancellation_reason ||
      "A cancellation factor is present, so this should be read as reduced or partial Mangal Dosha."
    );
  }
  return "No strong cancellation factor was detected in this quick check. Use the full kundali context before making a final marriage decision.";
}

function MangalRemedies({ dosha }: { dosha: MangalDosha }) {
  if (!dosha.remedies || dosha.remedies.length === 0) return null;

  return (
    <MangalDetailSection title="Remedies">
      <div className="space-y-2">
        {dosha.remedies.map((remedy, i) => (
          <div
            key={i}
            className="flex items-start gap-2 p-3 rounded-lg"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              border: "1px solid var(--ish-border)",
            }}
          >
            {/* inert-by-design: remedy category label (e.g. "mantra", "gemstone") — not a Vedic concept requiring drilldown; the remedy text below explains the action */}
            <span
              className="text-[12px] font-semibold px-2 py-0.5 rounded"
              style={{
                backgroundColor: "var(--ish-accent-light)",
                color: "var(--ish-accent)",
              }}
            >
              {remedy.remedy_type}
            </span>
            <span className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
              {remedy.reason}
            </span>
          </div>
        ))}
      </div>
    </MangalDetailSection>
  );
}
